import { useEffect, useState } from 'react';
import '../Admin.scss';
import axios from 'axios';
import MainURL from '../../MainURL';
import Pagination from '../../components/Pagination';
import Loading from '../../components/Loading';
import type { RecruitBoardConfig } from '../../screens/recruit/common/RecruitTypes';
import RecruitSavedSearchBar from './RecruitSavedSearchBar';
import RecruitSavedListTable from './RecruitSavedListTable';
import RecruitSavedEditModal from './RecruitSavedEditModal';
import RecruitSavedMoveModal from './RecruitSavedMoveModal';
import type { RecruitSavedListItem } from './recruitSavedAdminShared';

type Props = {
  config: RecruitBoardConfig;
  manageTitle: string;
};

export default function RecruitCategorySavedManage({ config, manageTitle }: Props) {
  const [list, setList] = useState<RecruitSavedListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchWord, setSearchWord] = useState('');
  const [activeSearchWord, setActiveSearchWord] = useState('');
  const [editItem, setEditItem] = useState<RecruitSavedListItem | null>(null);
  const [moveItem, setMoveItem] = useState<RecruitSavedListItem | null>(null);

  const recruitType = config.simplePostType === 'institute' ? 'institute' : 'church';

  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = activeSearchWord
        ? await axios.post(`${MainURL}/${config.apiBase}/recruitsearchunified`, {
            searchWord: activeSearchWord,
          })
        : await axios.get(`${MainURL}/${config.apiBase}/getrecruitdata/${currentPage}`);

      const resultData = res.data?.resultData;
      const rows = Array.isArray(resultData) ? resultData : resultData ? [resultData] : [];

      setList(rows);
      setTotalCount(res.data?.totalCount || rows.length);
    } catch (error) {
      console.error('구인 목록 조회 오류:', error);
      setList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPosts();
  }, [currentPage, activeSearchWord, config.apiBase]);

  const deletePost = async (item: RecruitSavedListItem) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const res = await axios.post(`${MainURL}/${config.apiBase}/deleterecruit`, {
        postId: item.id,
        userAccount: item.userAccount || '',
      });

      if (res.data) {
        alert('삭제되었습니다.');
        void fetchPosts();
      } else {
        alert('삭제에 실패했습니다. (작성자 계정이 일치하지 않을 수 있습니다)');
      }
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleSearch = () => {
    setActiveSearchWord(searchWord.trim());
    setCurrentPage(1);
  };

  const handleResetSearch = () => {
    setSearchWord('');
    setActiveSearchWord('');
    setCurrentPage(1);
  };

  return (
    <div className="admin-board-manage">
      <p className="admin-board-manage__desc">
        {manageTitle} 저장된 구인 공고를 조회·수정·삭제·다른 게시판으로 이동할 수 있습니다.{' '}
        <a href={config.listPath} target="_blank" rel="noreferrer">
          사이트 게시판 보기
        </a>
      </p>

      <RecruitSavedSearchBar
        searchWord={searchWord}
        onSearchWordChange={setSearchWord}
        onSearch={handleSearch}
        onReset={handleResetSearch}
      />

      {loading ? (
        <Loading />
      ) : (
        <>
          <RecruitSavedListTable
            items={list}
            totalCount={totalCount}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            isSearchMode={Boolean(activeSearchWord)}
            onView={(item) => window.open(`${config.detailPath}/${item.id}`, '_blank', 'noopener,noreferrer')}
            onEdit={(item) => setEditItem(item)}
            onMove={(item) => setMoveItem(item)}
            onDelete={deletePost}
          />

          {editItem?.id != null ? (
            <RecruitSavedEditModal
              recruitType={recruitType}
              apiBase={config.apiBase}
              config={config}
              itemId={editItem.id}
              userAccount={editItem.userAccount}
              onClose={() => setEditItem(null)}
              onSaved={() => {
                setEditItem(null);
                void fetchPosts();
              }}
            />
          ) : null}

          <RecruitSavedMoveModal
            sourceBoard={recruitType}
            item={moveItem}
            onClose={() => setMoveItem(null)}
            onMoved={() => void fetchPosts()}
          />

          {!activeSearchWord && totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
}
